/**
 * Reset Sync Confirmation Modal
 *
 * Confirmation dialog for resetting cloud sync data.
 * Requires user to type "RESET" to confirm the destructive action.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert
} from '@enfomentis/ui';
import type { ProviderKey } from '@/types/sync/ProviderTypes';

export interface ResetSyncConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when user confirms reset action */
  onConfirm: () => Promise<void>;
  /** Whether the reset operation is in progress */
  isProcessing: boolean;
  /** Optional provider name for context in warning message */
  providerKey?: ProviderKey;
}

/**
 * Reset Sync Confirmation Modal
 *
 * Requires explicit confirmation by typing "RESET" before allowing
 * the user to overwrite cloud sync data with local data.
 */
export const ResetSyncConfirmationModal: React.FC<ResetSyncConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing,
  providerKey
}) => {
  const [confirmationText, setConfirmationText] = useState('');

  // Clear input when modal closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationText('');
    }
  }, [isOpen]);

  // Case-insensitive comparison with trim
  const isConfirmed = confirmationText.trim().toUpperCase() === 'RESET';

  // Get provider display name
  const providerDisplayName = providerKey === 'google-drive'
    ? 'Google Drive'
    : providerKey === 'local'
      ? 'Local Directory'
      : 'cloud storage';

  const handleConfirm = async (): Promise<void> => {
    if (isConfirmed) {
      await onConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isProcessing}
      hideCloseButton={isProcessing}
      placement="top"
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-danger" />
          <span>Reset Cloud Sync Data?</span>
        </ModalHeader>

        <ModalBody className="space-y-4">
          <Alert color="danger" variant="bordered">
            <div className="space-y-2">
              <p className="text-sm font-medium">
                This will replace all cloud sync data with your current local data.
              </p>
              <p className="text-sm">
                Your cloud data will be archived before replacement.
              </p>
              <p className="text-sm font-semibold mt-3">
                I understand this will create a fresh dataset in {providerDisplayName}.
              </p>
            </div>
          </Alert>

          <div className="space-y-2">
            <label htmlFor="reset-confirmation" className="text-sm font-medium">
              To confirm, type <span className="font-mono font-bold">RESET</span> below:
            </label>
            <Input
              id="reset-confirmation"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type RESET to confirm"
              isDisabled={isProcessing}
              autoComplete="off"
              autoFocus
            />
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="flat"
            onPress={onClose}
            isDisabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            variant="solid"
            onPress={handleConfirm}
            isDisabled={!isConfirmed || isProcessing}
            isLoading={isProcessing}
          >
            {isProcessing ? 'Resetting...' : 'Reset Sync Data'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
